import React from 'react';
import { secureErrorHandler } from '../../utils/secureErrorHandler';
import './ErrorBoundary.css';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  resetKey: number;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; retry: () => void }>;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      resetKey: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo });

    // Use secure error handler
    const secureError = secureErrorHandler.handleError(error, {
      route: window.location.pathname,
      action: 'react_error_boundary',
      additionalInfo: {
        componentStack: errorInfo.componentStack,
        errorBoundary: true
      }
    });

    console.error('ErrorBoundary caught an error:', {
      id: secureError.id,
      message: secureError.userMessage,
      type: secureError.type,
      severity: secureError.severity
    });
  }

  handleRetry = () => {
    // Increment resetKey to force a full remount of the children subtree,
    // ensuring stale component state doesn't silently re-trigger the error.
    this.setState((prev) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      resetKey: prev.resetKey + 1,
    }));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error!} retry={this.handleRetry} />;
      }

      return <DefaultErrorFallback error={this.state.error!} retry={this.handleRetry} />;
    }

    // key forces a full remount after retry, clearing any stale subtree state
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}

interface ErrorFallbackProps {
  error: Error;
  retry: () => void;
}

const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({ error, retry }) => {
  const isNetworkError = error.message.includes('network') || error.message.includes('fetch');
  const isAuthError = error.message.includes('auth') || error.message.includes('permission');

  return (
    <div className="error-boundary">
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <h1 className="error-title">Oops! Something went wrong</h1>
        
        <div className="error-message">
          {isNetworkError ? (
            <p>It looks like there's a network issue. Please check your connection and try again.</p>
          ) : isAuthError ? (
            <p>There was an authentication issue. Please try refreshing the page or logging in again.</p>
          ) : (
            <p>We encountered an unexpected error. Please try again or refresh the page.</p>
          )}
        </div>

        <div className="error-actions">
          <button onClick={retry} className="retry-button">
            Try Again
          </button>
          <button 
            onClick={() => window.location.href = '/dashboard'} 
            className="home-button"
          >
            Go to Dashboard
          </button>
          <button 
            onClick={() => window.location.reload()} 
            className="refresh-button"
          >
            Refresh Page
          </button>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <details className="error-details">
            <summary>Error Details (Development)</summary>
            <pre className="error-stack">
              <strong>Error:</strong> {error.name}: {error.message}
              {error.stack && (
                <>
                  <br /><br />
                  <strong>Stack Trace:</strong>
                  <br />
                  {error.stack}
                </>
              )}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};